import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FiEye,
    FiEyeOff,
    FiShield,
    FiUsers,
    FiCalendar,
    FiBriefcase,
} from "react-icons/fi";

import logo from "../../assets/images/expo-pilot-logo.png";
import axiosInstance from "../../utils/api";

const Login = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError("");
        setLoading(true);

        try {
            const res = await axiosInstance.post("/auth/login", {
                email,
                password,
            });

            const accessToken = res?.data?.accessToken;
            const refreshToken = res?.data?.refreshToken;

            const userRole = res?.data?.user?.role || res?.data?.role;

            const normalizedRole =
                typeof userRole === "string" ? userRole.toLowerCase() : "";

            if (normalizedRole !== "standiste" && normalizedRole !== "contractor") {
                localStorage.removeItem("token");
                localStorage.removeItem("refreshToken");
                localStorage.removeItem("role");
                localStorage.removeItem("userId");

                throw new Error("Invalid credentials");
            }

            localStorage.setItem("token", accessToken);
            localStorage.setItem("refreshToken", refreshToken);
            localStorage.setItem("role", normalizedRole);
            localStorage.setItem("userId", res?.data?.user?.id);

            navigate("/dashboard");
        } catch (err) {
            if (err?.response?.status === 429) {
                setError("Too many login attempts. Please wait and try again.");
            } else if (err?.response?.status === 401) {
                setError("Invalid email or password");
            } else {
                setError("Something went wrong. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-[#020617] via-[#0f172a] to-[#111827] px-4 py-10">

            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-sky-500/10 rounded-full blur-3xl animate-pulse" />

                <div
                    className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-indigo-500/10 rounded-full blur-3xl animate-pulse"
                    style={{ animationDelay: "1s" }}
                />
            </div>

            <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b20_1px,transparent_1px),linear-gradient(to_bottom,#1e293b20_1px,transparent_1px)] bg-[size:4rem_4rem]" />

            <div className="relative z-10 flex items-center justify-center min-h-screen">
                <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">

                    <div className="hidden lg:flex items-center justify-center">
                        <div className="max-w-xl">

                            <div className="inline-flex items-center justify-center rounded-3xl bg-white border border-slate-200 px-8 py-5 shadow-2xl mb-10">
                                <img
                                    src={logo}
                                    alt="Expo Pilot"
                                    className="w-48 object-contain"
                                />
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <p className="text-sm uppercase tracking-[0.35em] text-sky-400 font-semibold mb-4">
                                        Expo Workforce Platform
                                    </p>

                                    <h1 className="text-5xl xl:text-6xl font-bold leading-tight text-white">
                                        Workforce Management
                                        <span className="block bg-gradient-to-r from-sky-400 via-cyan-300 to-indigo-400 bg-clip-text text-transparent">
                                            For Stand Builders & Contractors
                                        </span>
                                    </h1>

                                    <p className="mt-6 text-slate-300 text-lg leading-relaxed">
                                        Manage your staff, supervisors, schedules, access,
                                        and exhibition workforce operations from one secure platform.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-center">
                        <form
                            onSubmit={handleSubmit}
                            className="w-full max-w-md bg-white/95 backdrop-blur-2xl border border-white/20 rounded-[32px] shadow-[0_20px_80px_rgba(0,0,0,0.35)] px-8 py-10"
                        >

                            <div className="lg:hidden flex justify-center mb-8">
                                <div className="rounded-3xl bg-white border border-slate-200 px-6 py-4 shadow-lg">
                                    <img
                                        src={logo}
                                        alt="Expo Pilot"
                                        className="w-36 object-contain"
                                    />
                                </div>
                            </div>

                            <div className="mb-8">
                                <h2 className="text-3xl font-bold text-slate-900">
                                    Welcome Back
                                </h2>

                                <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                                    Sign in to manage your staff.
                                </p>
                            </div>

                            {error && (
                                <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                    {error}
                                </div>
                            )}

                            <div className="mb-5">
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-3">
                                    Email Address
                                </label>

                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="admin@expopilot.com"
                                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10"
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-3">
                                    Password
                                </label>

                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Enter your password"
                                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3.5 pr-12 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10"
                                    />

                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 transition hover:text-slate-700"
                                    >
                                        {showPassword ? (
                                            <FiEyeOff size={18} />
                                        ) : (
                                            <FiEye size={18} />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="group relative overflow-hidden w-full rounded-2xl bg-gradient-to-r from-indigo-500 via-sky-500 to-blue-500 py-4 text-sm font-semibold text-white shadow-xl transition-all duration-300 hover:scale-[1.01] hover:shadow-2xl disabled:opacity-60 disabled:hover:scale-100"
                            >
                                <span className="relative z-10">
                                    {loading ? "Signing in..." : "Sign In"}
                                </span>

                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition bg-white/10" />
                            </button>

                            <div className="mt-8 pt-6 border-t border-slate-200">
                                <p className="text-center text-xs text-slate-500">
                                    © {new Date().getFullYear()} Expo Pilot. All rights reserved.
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;